import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { CustomProduct, PriceKind, SelectedPrice, TicketType } from '@product/const';
import { PriceService, SelectedPriceService, TicketFacade } from '@product/data-access';
import { ProductPipesModule } from '@product/utils';
import { PersonalDataAgreeTextCallbackComponent, PhoneAuthorizedUserDirective } from '@sdvor/shared/ui';
import {
  PhoneMaskDirective,
  SdBottomSheetService,
  SdModalService,
  SdvorButtonComponent,
  SdvorInputComponent,
} from '@sdvor/shared/ui/v2';
import { ExtendedCustomFormValidators } from '@shared/helpers';
import { DeviceService } from '@shared/services';
import { filter, map } from 'rxjs';
import { ProductItemCounterComponent } from '../product-item-counter';

@Component({
  selector: 'sd-product-transit-request',
  imports: [
    ProductItemCounterComponent,
    ProductPipesModule,
    ReactiveFormsModule,
    SdvorButtonComponent,
    SdvorInputComponent,
    PersonalDataAgreeTextCallbackComponent,
    PhoneMaskDirective,
    PhoneAuthorizedUserDirective,
  ],
  templateUrl: './product-transit-request.component.html',
  styleUrls: ['./product-transit-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductTransitRequestComponent implements OnInit, OnDestroy {
  protected product = input.required<CustomProduct>();
  protected quantity = signal<number>(1);

  protected form = new FormGroup({
    phone: new FormControl('', {
      validators: [ExtendedCustomFormValidators.phoneValidator],
      updateOn: 'change',
    }),
    quantity: new FormControl(1, [Validators.maxLength(60)]),
  });

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ticketFacade = inject(TicketFacade);
  private readonly priceService = inject(PriceService);
  private readonly sdModalService = inject(SdModalService);
  private readonly sdBottomSheetService = inject(SdBottomSheetService);
  private readonly selectedPriceService = inject(SelectedPriceService);
  private readonly deviceService = inject(DeviceService);

  private formState = toSignal(this.ticketFacade.getProcess(), { initialValue: undefined });

  protected productTransitPrice = signal<SelectedPrice | null>(null);

  protected selectedUnitCode = computed(() => {
    const unit = this.productTransitPrice()?.unit;

    if (!unit) return;

    return unit.code;
  });
  protected totalPrice = computed(() => {
    const transitPrice = this.productTransitPrice();
    const quantity =
      this.quantity() < (transitPrice?.multiplicity || 1) ? transitPrice?.multiplicity || 1 : this.quantity();

    if (!transitPrice || !transitPrice.value || !quantity) {
      return '0';
    }

    const total = transitPrice.value * quantity;

    return this.formattedPrice(total);
  });

  protected isSuccessState = computed(() => this.formState()?.success === true);
  protected isSdvorApp = computed(() => this.deviceService.isSdvorApp());

  ngOnInit(): void {
    this.loadTransitPrice();

    this.priceService.loadPricesIfNeed(this.product().code);

    this.form
      .get('quantity')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.quantity.set(value ?? 1);
      });

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeModal();
      }
    });
  }

  ngOnDestroy(): void {
    this.ticketFacade.resetProcess();
  }

  private loadTransitPrice(): void {
    this.selectedPriceService
      .getPricesBySelectedUnit(this.product().code)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((prices) => prices.find(({ kind }) => kind === PriceKind.TRANSIT) as SelectedPrice),
        filter(Boolean),
      )
      .subscribe((price) => {
        this.productTransitPrice.set(price);

        const currentQuantity = price.multiplicity || 1;
        this.form.patchValue({
          quantity: currentQuantity,
        });
        this.quantity.set(currentQuantity);
      });
  }

  private formattedPrice(total: number): string {
    return total.toLocaleString().replace(',', '.');
  }

  protected selectUnitCode(unitCode: string): void {
    if (unitCode === this.selectedUnitCode()) return;

    this.selectedPriceService.selectPriceByUnitCode(this.product().code, unitCode);
  }

  protected onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();

      return;
    }

    const { phone, quantity } = this.form.value as {
      phone: string;
      quantity: number;
    };
    const transitPrice = this.productTransitPrice();

    if (!transitPrice || !transitPrice.unit || !transitPrice.formattedValue) return;

    this.ticketFacade.requestTransitPrice({
      unitCode: transitPrice.unit?.code,
      quantity,
      phone,
      productCode: this.product().code,
      transitPrice: transitPrice.formattedValue,
      type: TicketType.FactoryPrice,
    });
  }

  protected closeModal(): void {
    this.sdModalService.closeActiveModal();
    this.sdBottomSheetService.close();
  }

  protected onPhoneFocus(): void {
    this.form.controls.phone.setErrors(null);
    this.form.controls.phone.markAsUntouched();
  }

  protected onPhoneBlur(): void {
    this.form.controls.phone.markAsTouched();
    this.form.controls.phone.updateValueAndValidity();
  }
}
